import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconSuccess, IconRocket, IconInvoice, IconClients, IconProducts, IconMap, IconSettings, IconInfo } from '../../../icons';

interface CompletionStepProps {
  companyName: string;
  onFinish: (startTour: boolean) => Promise<void>;
}

const CompletionStep: React.FC<CompletionStepProps> = ({
  companyName,
  onFinish,
}) => {
  const { t } = useTranslation('onboarding');
  const [isCompleting, setIsCompleting] = useState(false);

  const handleComplete = async (startTour: boolean) => {
    setIsCompleting(true);
    try {
      await onFinish(startTour);
    } catch (err) {
      // Handle error silently
    } finally {
      setIsCompleting(false);
    }
  };

  return (
    <div className="text-center max-w-2xl mx-auto">
      {/* Success Animation */}
      <div className="mb-8 relative">
        <div className="w-24 h-24 mx-auto bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-2xl animate-pulse">
          <IconSuccess size={48} className="text-primary" />
        </div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-32 bg-green-500/20 rounded-full animate-ping" />
      </div>

      {/* Congratulations */}
      <h2 className="text-3xl font-bold text-primary mb-4">
        {t('steps.completion.title')} 🎉
      </h2>
      
      <p className="text-lg text-secondary mb-2">
        {t('steps.completion.subtitle', { company: companyName })}
      </p>
      
      <p className="text-muted mb-8">
        {t('steps.completion.description')}
      </p>

      {/* What's Next */}
      <div className="bg-[rgb(var(--surface-raised-rgb)/0.5)] rounded-xl p-6 mb-8 text-left border border-hairline">
        <h3 className="text-lg font-semibold text-primary mb-4 flex items-center">
          <IconRocket className="mr-2 text-accent-ink" />
          {t('steps.completion.whatsNext')}
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start p-3 bg-[rgb(var(--surface-base-rgb)/0.5)] rounded-lg">
            <span className="flex-shrink-0 w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center mr-3">
              <IconInvoice className="text-accent-ink" />
            </span>
            <div>
              <p className="text-primary font-medium">{t('steps.completion.action1Title')}</p>
              <p className="text-muted text-sm">{t('steps.completion.action1Desc')}</p>
            </div>
          </div>
          
          <div className="flex items-start p-3 bg-[rgb(var(--surface-base-rgb)/0.5)] rounded-lg">
            <span className="flex-shrink-0 w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center mr-3">
              <IconClients className="text-[var(--tone-success-fg)]" />
            </span>
            <div>
              <p className="text-primary font-medium">{t('steps.completion.action2Title')}</p>
              <p className="text-muted text-sm">{t('steps.completion.action2Desc')}</p>
            </div>
          </div>
          
          <div className="flex items-start p-3 bg-[rgb(var(--surface-base-rgb)/0.5)] rounded-lg">
            <span className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center mr-3">
              <IconProducts className="text-[var(--tone-warning-fg)]" />
            </span>
            <div>
              <p className="text-primary font-medium">{t('steps.completion.action3Title')}</p>
              <p className="text-muted text-sm">{t('steps.completion.action3Desc')}</p>
            </div>
          </div>
          
          <div className="flex items-start p-3 bg-[rgb(var(--surface-base-rgb)/0.5)] rounded-lg">
            <span className="flex-shrink-0 w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center mr-3">
              <IconMap className="text-accent-ink" />
            </span>
            <div>
              <p className="text-primary font-medium">{t('steps.completion.action4Title')}</p>
              <p className="text-muted text-sm">{t('steps.completion.action4Desc')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Profile Settings Hint */}
      <div className="bg-[var(--tone-accent-bg)] rounded-xl p-4 mb-8 text-left border border-[var(--tone-accent-border)]">
        <div className="flex items-start">
          <span className="flex-shrink-0 w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center mr-3">
            <IconSettings className="text-accent-ink" />
          </span>
          <div>
            <p className="text-primary font-medium flex items-center">
              <IconInfo className="mr-2 text-accent-ink" />
              {t('steps.completion.profileHintTitle')}
            </p>
            <p className="text-muted text-sm mt-1">
              {t('steps.completion.profileHintDesc')}
            </p>
            <ul className="text-muted text-sm mt-2 space-y-1">
              <li>• {t('steps.completion.profileHint1')}</li>
              <li>• {t('steps.completion.profileHint2')}</li>
              <li>• {t('steps.completion.profileHint3')}</li>
            </ul>
          </div>
        </div>
      </div>

      {/* CTA Buttons — M-UX-G1: Aha-Moment (erste Rechnung) ist die primäre, aktive
          Handlung; die Tour ist nur noch sekundär (Aufschub statt Wert). */}
      <div className="flex flex-col sm:flex-row justify-center gap-4">
        <button
          onClick={() => handleComplete(false)}
          disabled={isCompleting}
          data-testid="onboarding-completion-primary-cta"
          className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-indigo-600 to-indigo-700 text-[#0C1322] font-semibold rounded-xl shadow-lg hover:shadow-indigo-500/30 transform hover:scale-[1.02] transition-all duration-200 disabled:opacity-50"
        >
          <IconInvoice className="mr-2" />
          {isCompleting ? t('common.loading') : t('steps.completion.createFirstInvoice')}
        </button>

        <button
          onClick={() => handleComplete(true)}
          disabled={isCompleting}
          data-testid="onboarding-completion-secondary-cta"
          className="inline-flex items-center justify-center px-8 py-4 bg-surface-inset text-primary font-semibold rounded-xl hover:bg-surface-hover transition-all duration-200 disabled:opacity-50"
        >
          <IconMap className="mr-2" />
          {isCompleting ? t('common.loading') : t('steps.completion.startTour')}
        </button>
      </div>
    </div>
  );
};

export default CompletionStep;

